import React, { useEffect, useRef, useState } from 'react';

import { Dropdown } from 'react-bootstrap';
import { GetMaxStage, GetWellheadsByJobId_getWellheadsByJobId as Wellheads } from '../../../../types/schemaTypes';
import '../JobStageSummary.scss';
import { blockInvalidChar } from '../../../../components/_common/blockInvalidChar';
import { useLazyQuery } from '@apollo/client/react';
import { GET_MAX_STAGE } from '../../../../graphql/client/queries';
import { Loading } from '../../../_common';

interface StagesToAddProps {
    index: number;
    wellHead: string;
    stageNum: number;
    stageSheetDescrip: string;
    maxStageNum: number;
}

interface AddStageModalRowProps {
    jobId: number;
    index: number;
    wellheads: Wellheads[];
    fracType: 'Single' | 'Simul' | 'Dual' | undefined;
    stagesToAdd: StagesToAddProps[] | undefined;
    stageCount: number;
    setStageCount: React.Dispatch<React.SetStateAction<number>>;
    setStagesToAdd: React.Dispatch<React.SetStateAction<StagesToAddProps[] | undefined>>;
    showDelete: boolean;
    wellheadError: number[] | undefined;
    setWellheadError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberNotAnIntegerError: number[] | undefined;
    setStageNumberNotAnIntegerError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberOutOfBoundsError: number[] | undefined;
    setStageNumberOutOfBoundsError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberDuplicateError: number[] | undefined;
    setStageNumberDuplicateError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    wellheadDuplicateError: number[] | undefined;
    setWellheadDuplicateError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    runsheetDuplicateError: number[] | undefined;
    setRunsheetDuplicateError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    stageNumberMaxError: number[] | undefined;
    setStageNumberMaxError: React.Dispatch<React.SetStateAction<number[] | undefined>>;
}

const AddStageModalRow = ({
    jobId,
    index,
    wellheads,
    fracType,
    stagesToAdd,
    setStagesToAdd,
    stageCount,
    setStageCount,
    showDelete,
    wellheadError,
    setWellheadError,
    stageNumberNotAnIntegerError,
    setStageNumberNotAnIntegerError,
    stageNumberOutOfBoundsError,
    setStageNumberOutOfBoundsError,
    stageNumberDuplicateError,
    setStageNumberDuplicateError,
    wellheadDuplicateError,
    setWellheadDuplicateError,
    runsheetDuplicateError,
    setRunsheetDuplicateError,
    stageNumberMaxError,
    setStageNumberMaxError,
}: AddStageModalRowProps): React.ReactElement => {
    const [wellHead, setWellhead] = useState<string>();
    const [stageNumber, setStageNumber] = useState(-1);
    const [maxStageNumber, setMaxStageNumber] = useState(-1);
    const [runsheet, setRunsheet] = useState<'Runsheet 1' | 'Runsheet 2'>('Runsheet 1');
    const [isDeleted, setIsDeleted] = useState(false);
    const processedMaxStageRef = useRef(false);

    const stageToAdd: StagesToAddProps = {
        index: index,
        wellHead: wellheads.length === 1 && wellheads[0].wellHead ? wellheads[0].wellHead : '',
        stageNum: -1,
        maxStageNum: -1,
        stageSheetDescrip: 'Runsheet 1',
    };

    useEffect(() => {
        setStagesToAdd(prev => {
            if (!prev) return [stageToAdd];
            if (prev.some(s => s.index === index)) return prev;
            return [...prev, stageToAdd];
        });
    }, []);

    const errorMessage = {
        wellHeadErrorMessage: 'Select a wellhead',
        stageNumberErrorMessage: 'Enter a valid stage number',
        stageNumberOutOfBoundErrorMessage: 'Stage number must not be more than ',
        stageNumberMaxErrorMessage: 'Stage # cannot be more than ',
        stageNumberDuplicateMessage: 'Wellhead - Stage # combination must be unique',
        wellheadDuplicateErrorMessage: 'Wellhead must be unique',
        runnerNumberDuplicateMessage: 'Wellhead - Stage # combination must be unique',
    };
    const handleOnChangeStageNumber = (stageNumber, maxStage) => {
        if (Number.isNaN(stageNumber) || !Number.isInteger(stageNumber) || stageNumber === 0) stageNumber = -1;
        setStageNumber(stageNumber);

        setStagesToAdd(prev => {
            if (!prev) return prev;
            return prev.map(stage =>
                stage.index === index && stageNumber
                    ? { ...stage, stageNum: stageNumber, maxStageNum: maxStage }
                    : stage,
            );
        });

        if (stageNumberNotAnIntegerError?.includes(index)) {
            stageNumberNotAnIntegerError.splice(stageNumberNotAnIntegerError.indexOf(index), 1);
            setStageNumberNotAnIntegerError(stageNumberNotAnIntegerError);
        }
        if (stageNumberOutOfBoundsError?.includes(index)) {
            stageNumberOutOfBoundsError.splice(stageNumberOutOfBoundsError.indexOf(index), 1);
            setStageNumberOutOfBoundsError(stageNumberOutOfBoundsError);
        }
        if (stageNumberMaxError?.includes(index)) {
            stageNumberMaxError.splice(stageNumberMaxError.indexOf(index), 1);
            setStageNumberMaxError(stageNumberMaxError);
        }
        if (stageNumberDuplicateError?.includes(index)) {
            stageNumberDuplicateError.splice(stageNumberDuplicateError.indexOf(index), 1);
            setStageNumberDuplicateError(stageNumberDuplicateError);
        }
        if (wellheadDuplicateError?.includes(index)) {
            wellheadDuplicateError.splice(wellheadDuplicateError.indexOf(index), 1);
            setWellheadDuplicateError(wellheadDuplicateError);
        }
        if (runsheetDuplicateError?.includes(index)) {
            runsheetDuplicateError.splice(runsheetDuplicateError.indexOf(index), 1);
            setRunsheetDuplicateError(runsheetDuplicateError);
        }
    };

    const getStageCount = () => {
        for (const wellhead of wellheads) {
            if (wellhead && wellhead.wellHead === wellHead) return wellhead.stages;
        }
    };
    const [getParamsForGetMaxStage, { data: getMaxStageData, loading: getMaxStageLoading }] = useLazyQuery<GetMaxStage>(
        GET_MAX_STAGE,
        { fetchPolicy: 'no-cache' },
    );

    useEffect(() => {
        if (getMaxStageLoading || !getMaxStageData?.getMaxStage || processedMaxStageRef.current) return;
        processedMaxStageRef.current = true;
        const count = getMaxStageData.getMaxStage.count;
        const nextCount = count + 1;
        setMaxStageNumber(nextCount);
        handleOnChangeStageNumber(nextCount, nextCount);
    }, [getMaxStageData, getMaxStageLoading]);

    const handleOnChangeWellhead = wellhead => {
        setWellhead(wellhead);

        setStagesToAdd(prev => {
            if (!prev) return prev;
            return prev.map(stage =>
                stage.index === index ? { ...stage, wellHead: wellhead } : stage,
            );
        });

        if (wellheadError?.includes(index)) {
            wellheadError.splice(wellheadError.indexOf(index), 1);
            setWellheadError(wellheadError);
        }
        processedMaxStageRef.current = false;
        getParamsForGetMaxStage({
            variables: {
                jobId: jobId,
                wellHead: encodeURIComponent(wellhead),
            },
        });
    };

    const handleOnChangeRunsheet = runsheet => {
        setRunsheet(runsheet);

        setStagesToAdd(prev => {
            if (!prev) return prev;
            return prev.map(stage =>
                stage.index === index && runsheet
                    ? { ...stage, stageSheetDescrip: runsheet }
                    : stage,
            );
        });
    };

    const handleOnDelete = () => {
        setIsDeleted(true);
        setStageCount(stageCount - 1);
        setStagesToAdd(prev => prev?.filter(stage => stage.index !== index));
    };

    useEffect(() => {
        if (fracType != 'Dual' && runsheet !== 'Runsheet 1') handleOnChangeRunsheet('Runsheet 1');
    }, [fracType, runsheet]);

    useEffect(() => {
        if (wellheads.length === 1 && !wellHead) handleOnChangeWellhead(wellheads[0].wellHead);
    }, [wellheads, wellHead]);

    if (isDeleted) {
        return <></>;
    }

    return (
        <tr>
            <td className="AddStageModal-Column">
                <Dropdown>
                    <Dropdown.Toggle variant="outline-dark" id="dropdown-basic" className="AddStageModalRow-Dropdown">
                        <span>{wellHead ?? 'Select Wellhead'}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {wellheads.map((wellhead, index) => (
                            <Dropdown.Item
                                key={index}
                                onClick={() => wellhead.wellHead && handleOnChangeWellhead(wellhead.wellHead)}
                               // onSelect={() => wellhead.wellHead && handleOnChangeWellhead(wellhead.wellHead)}
                            >
                                {wellhead.wellHead}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                    <div className="AddStageModal-Error">
                        {wellheadError?.includes(index) && errorMessage.wellHeadErrorMessage}
                    </div>
                    <div className="AddStageModal-Error">
                        {wellheadDuplicateError?.includes(index) && errorMessage.wellheadDuplicateErrorMessage}
                    </div>
                </Dropdown>
            </td>
            <td className="AddStageModal-Column AddStageNumber-Container">
                {getMaxStageLoading && <Loading />}
                <input
                    type="number"
                    className="AddStageNumber"
                    //min={0}
                    onKeyDown={blockInvalidChar}
                    //disabled={true}
                    value={stageNumber === -1 ? '' : stageNumber}
                    onChange={element => handleOnChangeStageNumber(parseInt(element.target.value), maxStageNumber)}
                />
                <div className="AddStageModal-Error">
                    {stageNumberNotAnIntegerError?.includes(index) && errorMessage.stageNumberErrorMessage}
                </div>
                <div className="AddStageModal-Error">
                    {stageNumberOutOfBoundsError?.includes(index) &&
                        errorMessage.stageNumberOutOfBoundErrorMessage + getStageCount()}
                </div>
                <div className="AddStageModal-Error">
                    {stageNumberMaxError?.includes(index) && errorMessage.stageNumberMaxErrorMessage + maxStageNumber}
                </div>
                <div className="AddStageModal-Error">
                    {stageNumberDuplicateError?.includes(index) && errorMessage.stageNumberDuplicateMessage}
                </div>
            </td>
            <td className="AddStageModal-Column">
                <Dropdown>
                    <Dropdown.Toggle variant="outline-dark" id="dropdown-basic" className="AddStageModalRow-Dropdown">
                        <span>{runsheet ?? 'Runsheet 1'}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {fracType === 'Dual' ? (
                            <>
                                <Dropdown.Item
                                // onSelect={() => handleOnChangeRunsheet('Runsheet 1')}
                                onClick={() => handleOnChangeRunsheet('Runsheet 1')}
                                >
                                    Runsheet 1
                                </Dropdown.Item>
                                <Dropdown.Item 
                               // onSelect={() => handleOnChangeRunsheet('Runsheet 2')}
                               onClick={() => handleOnChangeRunsheet('Runsheet 2')}
                                >
                                    Runsheet 2
                                </Dropdown.Item>
                            </>
                        ) : (
                            <Dropdown.Item
                             //onSelect={() => handleOnChangeRunsheet('Runsheet 1')}

                             onClick={() => handleOnChangeRunsheet('Runsheet 1')}>
                                Runsheet 1
                            </Dropdown.Item>
                        )}
                    </Dropdown.Menu>
                </Dropdown>
                <div className="AddStageModal-Error">
                    {runsheetDuplicateError?.includes(index) && errorMessage.runnerNumberDuplicateMessage}
                </div>
            </td>
            <td className="AddStageModal-Column-Sm">
                {showDelete && (
                    <div className="AddStage-Delete-Container">
                        <i className="material-icons AddStage-Delete-Button" onClick={() => handleOnDelete()}>
                            cancel
                        </i>
                    </div>
                )}
            </td>
        </tr>
    );
};

export default AddStageModalRow;